/**
 * Cart drawer accessories upsell — checkbox rows add to cart and refresh the drawer
 * the same way product-form batch add does (sections + CartLinesUpdateEvent).
 *
 * Loaded once with cart-drawer (not inside morphable cart HTML) so the listener
 * is always active after empty → filled cart morphs.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const BOUND = 'data-m4aw-cart-acc-bound';

/**
 * @returns {{ cart_add_url: string, cart_url: string }}
 */
function getRoutes() {
  const routes = globalThis.Theme?.routes ?? {};
  return {
    cart_add_url: routes.cart_add_url || '/cart/add.js',
    cart_url: routes.cart_url || '/cart',
  };
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  }
  return sectionIds;
}

/**
 * @param {HTMLInputElement} input
 */
async function addAccessoryFromCheckbox(input) {
  if (!input.checked) return;
  if (input.dataset.adding === 'true') return;

  const row = input.closest('[data-cart-acc-row]');
  const variantId = input.getAttribute('data-variant-id');
  if (!variantId) {
    input.checked = false;
    return;
  }

  input.dataset.adding = 'true';
  row?.classList.add('is-loading');

  const sectionIds = getCartSectionIds();
  const routes = getRoutes();
  const deferred = CartLinesUpdateEvent.createPromise();

  // Dispatch on document so cart-items-component morphs (it skips events where target === itself)
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      document.dispatchEvent(
        new CartErrorEvent({
          error: data.message || data.description || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );

      const cartRes = await fetch(`${routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartRes.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: true,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-accessories-upsell',
        },
      });

      input.checked = false;
      row?.classList.remove('is-loading');
      delete input.dataset.adding;
      return;
    }

    const cartRes = await fetch(`${routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const cart = await cartRes.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: 'cart-accessories-upsell',
        sections: data.sections,
        didError: false,
      },
    });

    // Row is usually replaced by morph; clear loading if it survives
    row?.classList.remove('is-loading');
    delete input.dataset.adding;
  } catch (error) {
    console.error('[cart-accessories-upsell]', error);
    deferred.reject(error);
    input.checked = false;
    row?.classList.remove('is-loading');
    delete input.dataset.adding;
  }
}

function bind() {
  if (document.documentElement.hasAttribute(BOUND)) return;
  document.documentElement.setAttribute(BOUND, '');

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-cart-acc-add]')) return;
    addAccessoryFromCheckbox(target);
  });
}

bind();
